import React, { useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useBillingInfoContext } from '../../../../components/BillingInfoProvider';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Typography from '@material-ui/core/Typography';
import CompanyInformationForm from '../../../../components/CompanyInformationForm';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import decamelizeObject from '@afosto/utils/object/decamelizeObject';
import { subscription as subscriptionApi } from '@afosto/iam/api/Subscription';
import translations from './translations';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';

const CompanyInformation = () => {
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['iam:customer:read']);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [showEditDrawer, setShowEditDrawer] = useState(false);
  const {
    billingAddress,
    companyInformation,
    isFetchingBillingInfo,
    setCompanyInformation,
  } = useBillingInfoContext();

  const handleSaveCompanyInformation = async formData => {
    try {
      const response = await subscriptionApi.updateBillingInformation({
        ...decamelizeObject({
          ...formData,
          vatNumber: formData.vatNumber ?? '',
        }),
        address: { ...billingAddress },
      });
      const {
        data: { address, ...rest },
      } = response;

      setCompanyInformation(rest);
      setShowEditDrawer(false);

      enqueueSnackbar(intl.formatMessage(translations.feedback.companyInformationSaved), {
        variant: 'success',
      });
      return response;
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.companyInformationNotSaved), {
        variant: 'error',
      });
      return Promise.reject(err);
    }
  };

  if (!hasPermissions()) {
    return (
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <CardContent>
          <UnauthorizedComponent showActions={false} />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
          action={
            hasPermissions(['iam:customer:write']) ? (
              <Button variant="outlined" color="default" onClick={() => setShowEditDrawer(true)}>
                <FormattedMessage {...translations.edit.button} />
              </Button>
            ) : null
          }
        />
        <CardContent>
          {(!isFetchingBillingInfo &&
            Object.keys(companyInformation).map(key => (
              <Typography variant="body2" color="textSecondary" key={key}>
                {companyInformation[key]}
              </Typography>
            ))) || <SkeletonBody lines={4} style={{ maxWidth: 150 }} />}
        </CardContent>
      </Card>
      <FormDrawer
        title={intl.formatMessage(translations.edit.title)}
        isOpen={showEditDrawer}
        onClose={() => setShowEditDrawer(false)}
        drawerWidth="400px"
      >
        <CompanyInformationForm
          companyInformation={companyInformation}
          onSubmit={handleSaveCompanyInformation}
          onCancel={() => setShowEditDrawer(false)}
        />
      </FormDrawer>
    </>
  );
};

export default CompanyInformation;
